import React, {useRef} from "react";
import { HeadFC, PageProps, graphql } from "gatsby";
import PodcastHeader from "../components/Pages/Podcast/PodcastHeader";
import BlogDetails from "../components/BlogDetails/BlogDetails";
import QuoteCard from "../components/Cards/QuoteCard/QuoteCard";
import TextureOne from "../assets/podcast-textures/texture_background_AutomationExplanation.svg";
import TextureTwo from "../assets/podcast-textures/texture_background_AgileCoaches.svg";
import TextureThree from "../assets/podcast-textures/texture_background_AI_Live_Unbiased.svg";
import QuotationMarks from "../assets/icons/QuotationMarks.svg";
import CardIntersect from "../assets/intersects/ReviewerCardIntersect.svg";
import Accordion from "../components/Accordion/Accordion";
import AccordionItem from "../components/Accordion/AccordionItem";
import Image from "../interfaces/image";
import Author from "../interfaces/author";
import PodcastCard from "../components/Pages/Podcast/PodcastCard";
import { serialize, Node } from "../components/RichText/RichText";
import AuthorCardDesktop from "../components/Cards/AuthorCard/AuthorCardDesktop";
import { PodcastTypes } from "../enums/tags";
import formatDate from "../utils/transformers/formatDate";
import Meta from "../components/Meta/Meta";
import { transcript } from "../utils/transformers/cmsToWebinars";
import ResponsivePostsGallery from "../components/Pages/Insights/ResponsivePostsGallery";
import { PodcastTranscriptTemplate } from "../components/PodcastTranscriptTemplate";
import useHtmlToPdfPrinter from "../utils/useHtmlToPdfPrinter";
import createSlugFromTitle from "../utils/createSlugFromTitle";
import PaginatedContent from "../components/PaginatedContent/PaginatedContent";
import CanvasParticlesWrapper from "../components/CanvasParticles/CanvasParticlesWrapper";
import SEO from "../components/SEO/SEO";
import IPayload from "../interfaces/payload";
import TemplateContext from "../interfaces/templateContext";

interface Podcast {
  title: string;
  postDate: string;
  content: Node[];
  tags: Tag[];
  authors: Author[];
  image: Image;
  episodeNumber: string;
  audioUrl: string;
}

interface Tag {
  tag: string;
}

interface Payload {
  Podcasts: {
    docs: Podcast[];
  };
}

interface GetPodcastQuery {
  Payload: Payload;
}

interface PageConfiguration {
  headerGradient: string;
  headerImage: string;
  headerImageMobile?: string;
  headerImageStyles: string;
  headerTitle: string;
  headerTexture: React.ReactNode;
  podcastCardTexture: React.ReactNode;
  textureStyles: string;
}

interface PodcastPages {
  [key: string]: PageConfiguration;
}

const PodcastPages: PodcastPages = {
  [PodcastTypes.AutomationExplanation]: {
    headerGradient: "bg-at-insignths-podcast-one",
    headerImage:
      "https://atwebsite.blob.core.windows.net/images/podcast_Category_1.webp",
    headerImageMobile:
      "https://atwebsite.blob.core.windows.net/images/Mask-group.webp",
    headerImageStyles: "h-full",
    headerTitle: "Agile Podcast: Automation Explanation",
    headerTexture: <TextureOne className=" opacity-20 h-full w-full " />,
    textureStyles: "-top-[37px] right-[50%] h-[1000px] w-[1000px]",
    podcastCardTexture: <TextureOne className=" opacity-20  " />,
  },
  [PodcastTypes.AgilePodcast]: {
    headerGradient: "bg-at-insignths-podcast-three",
    headerImage:
      "https://atwebsite.blob.core.windows.net/images/Agile_CoachesC_Desktop.webp",
    headerImageStyles:
      "h-[13rem] md:h-[17rem] rotate-[30deg] md:rotate-0 translate-x-[-1rem] md:translate-x-0 translate-y-[2.5rem] md:translate-y-[6rem]",
    headerTitle: "Agile Podcast: Agile Coaches' Corner",
    headerTexture: <TextureTwo className=" opacity-20 h-full w-full" />,
    textureStyles: "-top-[37px] right-[50%] h-[1000px] w-[1000px]",
    podcastCardTexture: <TextureTwo className=" opacity-20  " />,
  },
  [PodcastTypes.Agile]: {
    headerGradient: "bg-at-insignths-podcast-three",
    headerImage:
      "https://atwebsite.blob.core.windows.net/images/Agile_CoachesC_Desktop.webp",
    headerImageStyles:
      "h-[13rem] md:h-[17rem] rotate-[30deg] md:rotate-0 translate-x-[-1rem] md:translate-x-0 translate-y-[2.5rem] md:translate-y-[6rem]",
    headerTitle: "Agile Podcast: Agile Coaches' Corner",
    headerTexture: <TextureTwo className=" opacity-20 h-full w-full" />,
    textureStyles: "-top-[37px] right-[50%] h-[1000px] w-[1000px]",
    podcastCardTexture: <TextureTwo className=" opacity-20  " />,
  },
  [PodcastTypes.AIPodcast]: {
    headerGradient: "bg-at-insignths-podcast-two",
    headerImage:
      "https://atwebsite.blob.core.windows.net/images/AI_Live_Unbiased_Desktop.webp",
    headerImageMobile:
      "https://atwebsite.blob.core.windows.net/images/AI_Live_Unbiased_Mobile.webp",
    headerImageStyles:
      "h-[13rem] md:h-[14rem] translate-y-[7.5rem] md:translate-y-[6rem]",
    headerTitle: "Agile Podcast: AI Live Unbiased",
    headerTexture: <TextureThree className=" opacity-20 h-full w-full " />,
    textureStyles: "-top-[118px] right-[50%] h-[1000px] w-[1000px]",
    podcastCardTexture: <TextureThree className=" opacity-20  " />,
  },
  [PodcastTypes.Default]: {
    headerGradient: "bg-at-insignths-podcast-two",
    headerImage:
      "https://atwebsite.blob.core.windows.net/images/podcast_Category_1.webp",
    headerImageMobile:
      "https://atwebsite.blob.core.windows.net/images/Mask-group.webp",
    headerImageStyles: "h-full",
    headerTitle: "Agile Podcast: AI Live Unbiased",
    headerTexture: <TextureThree className=" opacity-20 h-full w-full" />,
    textureStyles: "-top-[37px] right-[50%] h-[1000px] w-[1000px]",
    podcastCardTexture: <TextureThree className=" opacity-20  " />,
  },
};

export const Podcast: React.FC<{ data: GetPodcastQuery, pageContext: TemplateContext }> = (props) => {
  const data = props.data;
  const tag = ({ ...props.pageContext } as Tag).tag ?? "Default";
  const podcast = data.Payload.Podcasts.docs[0];
  const title = podcast.title;
  const dateText = podcast.postDate;
  const episodeNumber = podcast.episodeNumber;
  const imagePodcastCard = podcast.image.url;
  const audioUrl = podcast.audioUrl;
  const tags = podcast.tags.map((tag) => tag.tag);
  const dateFormated = formatDate(dateText);
  const agilePoints = transcript(podcast.content);
  const transcriptTemplateRef = useRef<HTMLDivElement>(null);
  const { print } = useHtmlToPdfPrinter();

  const handleTranscriptDownload = () => {
    const element = transcriptTemplateRef.current;
    if (!element) return;
    print({ domElement: element, documentName: `transcript-${createSlugFromTitle(title)}` });
  }

  return (
    <CanvasParticlesWrapper heightRatio={2} centerCanvasWrapper={false}>
      <PodcastHeader
        height="h-[318px]"
        width="w-full lg:max-w-[var(--max-website-width)] overflow-y-hidden"
        backgroundGradient={PodcastPages[tag].headerGradient}
        backgroundGradientHeight="h-full flex justify-center content-end items-start md:items-center"
        image={PodcastPages[tag].headerImage}
        imageMobile={PodcastPages[tag].headerImageMobile}
        classNameImage={PodcastPages[tag].headerImageStyles}
        texture={PodcastPages[tag].headerTexture}
        title={PodcastPages[tag].headerTitle}
        textureStyles={PodcastPages[tag].textureStyles}
        episode={`Ep. ${episodeNumber}`}
      />
      <div className="flex flex-col pt-10 px-2 xs:px-10 justify-center items-center">
        <h2 className="text-vibrant-green text-center">{title}</h2>
        <BlogDetails dateText={dateFormated} tags={tags} isColumn />
        <PaginatedContent height="288px" showAllOnClick showLessOnAll>
          {serialize({ children: podcast.content }) as JSX.Element | JSX.Element[]}
        </PaginatedContent>
        <div className="w-full flex flex-col justify-center h-full lg:flex-row">
          <div className="w-full lg:w-4/12 md:pr-5 pt-10">
            <div className="h-[600px] max-w-[400px] lg:max-w-none mx-auto">
              <PodcastCard
                image={imagePodcastCard}
                imageHeight="h-[55%]"
                textHeight="h-[55%]"
                gradient={PodcastPages[tag].headerGradient}
                svg={PodcastPages[tag].podcastCardTexture}
                audioUrl={audioUrl}
                title={title}
                onDownload={handleTranscriptDownload}
              />
            </div>
          </div>
          <div className=" w-full lg:w-8/12 pl-5 flex flex-col justify-center items-center">
            <div className="pt-10 hidden">
              <QuoteCard
                backgroundGradient="bg-at-quote-podcast-detail"
                quote="Optimized Artificial Intelligence is like having thousands, or even millions, of people brainstorming, prototyping, and testing ideas. "
                bottomRightSVG={
                  <QuotationMarks className="w-[70px] h-[70px] rotate-180 bottom-7" />
                }
                topLeftSVG={<QuotationMarks className="w-[70px] h-[70px]" />}
                classNameContent="p-[46px]"
                intersect={<CardIntersect />}
              />
            </div>
            {agilePoints && (
              <div className="w-full pt-10 md:pt-0 flex flex-col justify-center items-center">
                <h2 className="text-vibrant-green pt-10">
                  Transcript
                </h2>
                <p className="w-full border-[2px] rounded-full border-deep-blue mt-4"></p>
                <ul className="w-full">
                  {agilePoints.map((accordion, i) => (
                    <Accordion title={accordion.title} key={i}>
                      <ul>
                        <AccordionItem title={accordion.content} />
                      </ul>
                    </Accordion>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <div className="mt-10 flex flex-wrap justify-center items-center w-full gap-5 h-auto">
                <h2 className="font-ztGraftonRegular text-mobile-h2 md:text-desktop-h2 text-vibrant-green pt-10 pb-20 text-center w-11/12">
                  Speakers
                </h2>
                {Array.isArray(podcast.authors) &&
                  podcast.authors.length > 0 &&
                  podcast.authors.map((author, index) => (
                    <AuthorCardDesktop
                      key={index}
                      authorImage={author?.avatar?.url}
                      authorName={`${author?.prefix ?? ""} ${
                        author?.firstName
                      } ${author?.lastName}`}
                      authorRole={author?.jobTitle}
                      width="w-[400px]"
                      height="h-[670px]"
                      isAtSpeaker={author?.isAgileThinker}
                      isSpeaker
                      title={
                        author?.about
                          ? author?.about
                          : "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. "
                      }
                      content={
                        author?.resume
                          ? author?.resume
                          : "Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum."
                      }
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full ">
          <ResponsivePostsGallery title='Related' subtitle=''
            posts={ props.pageContext.related || []} linkTo=""
            className="px-5 lg:px-0 [&_.gallery-more]:hidden pt-8 [&>p]:hidden [&>h2]:pb-10 mb-32" />
        </div>
      </div>
      <div className="h-0 overflow-y-auto">
        <div ref={transcriptTemplateRef} className="pdf-container">
          <PodcastTranscriptTemplate title={title} transcripts={agilePoints || []} />
        </div>
      </div>
    </CanvasParticlesWrapper>
  );
};

const PodcastDetailPage: React.FC<PageProps<GetPodcastQuery, TemplateContext>> = (props) => (
  <Podcast data={props.data} pageContext={props.pageContext} />
);

export default PodcastDetailPage;

export const Head: HeadFC<IPayload, TemplateContext> = ( props ) => (
  <Meta>
    <SEO path={props.location?.pathname} 
      {...props.data.Payload?.Podcasts?.docs?.[0]?.meta} 
      title={props.data.Payload?.Podcasts?.docs?.[0]?.meta?.title || props.pageContext.title} 
    />
  </Meta>
);

export const data = graphql`
  query GetPodcast($id: String) {
    Payload {
      Podcasts(where: { id: { equals: $id } }) {
        docs {
          slug
          title
          postDate
          createdAt
          legacyPostDate
          content
          tags {
            tag
          }
          authors {
            avatar {
              alt
              url
            }
            firstName
            lastName
            jobTitle
            isAgileThinker
            prefix
            about
            resume
          }
          image {
            alt
            url
          }
          audioUrl
          episodeNumber
          meta {
            title
            description
            image {
              url
            }
            keywords
            robots
            schemamk
          }
        }
      }
    }
  }
`;
